import React, { useCallback, useEffect, useState } from "react";
import { Bot, ChevronDown, ChevronRight, Loader2, RefreshCw, CheckCircle2, PauseCircle, PlayCircle, Sparkles, FileText, ImageIcon, ListTree } from "lucide-react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import AdminErrorState from "./AdminErrorState";

/**
 * EnrichmentOverviewCard (iter180) — compact "at a glance" strip that
 * mirrors the presence-history pattern: one row per pipeline, always
 * visible, click a row to expand the details inline.
 *
 * Backend `/admin/enrichment/overview` returns everything in one shot
 * (30-s cached server-side) so this card doesn't cost anything even
 * when it auto-refreshes every 30 seconds.
 *
 * Expanded state per row shows:
 *   - Deeper progress line (X done · Y remaining · Z%)
 *   - Worker status + toggle link that jumps the admin into the full
 *     Data & Ingestion section by expanding its <details> element.
 */
const num = (v) => (typeof v === "number" ? v.toLocaleString("en-GB") : "—");

const ICONS = {
  descriptions: FileText,
  facilities: ListTree,
  facts: Sparkles,
  photos: ImageIcon,
};

function ProgressBar({ pct, done }) {
  return (
    <div className="relative h-1.5 w-full bg-[#eee] rounded-sm overflow-hidden" aria-hidden="true">
      <div
        className={`absolute inset-y-0 left-0 ${pct >= 100 ? "bg-[#0a7f3f]" : done > 0 ? "bg-[#ff4500]" : "bg-[#ccc]"}`}
        style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
      />
    </div>
  );
}

// Expand the "Data & Ingestion" collapsible section on the page so the
// operator can act on whichever pipeline they just clicked into.
const expandDataSection = () => {
  const el = document.querySelector('[data-testid="admin-section-data"]');
  if (!el) return;
  if (!el.open) el.open = true;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function EnrichmentOverviewCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    setErr(null);
    try {
      const { data: res } = await api.get("/admin/enrichment/overview");
      setData(res);
    } catch (e) {
      // silent on auto-refresh, banner on first failure
      if (!data) setErr(e);
      if (!data) toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const toggleWorker = async () => {
    if (!data?.worker) return;
    try {
      await api.post("/admin/auto-enrich/toggle", { enabled: !data.worker.enabled });
      toast.success(!data.worker.enabled ? "Auto-enrich started" : "Auto-enrich paused");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const worker = data?.worker;
  const rows = data?.rows || [];

  return (
    <section
      className="border hairline rounded-2xl p-4 sm:p-5 mb-4 bg-white"
      data-testid="enrichment-overview-card"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="eyebrow flex items-center gap-1.5 text-[#ff4500] mb-1">
            <Bot className="w-3 h-3" /> AI enrichment · progress at a glance
          </div>
          <h2 className="font-display text-lg sm:text-xl tracking-tight leading-tight">
            Campsite content pipelines
          </h2>
          <p className="text-[11px] text-[#666] mt-0.5 max-w-2xl">
            Live progress of the Claude-powered enrichment workers.
            Tap a row to see details and jump to the full controls.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {worker && (
            <button
              onClick={toggleWorker}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] mono uppercase tracking-wider rounded-xl ${worker.enabled ? "bg-[#0a7f3f] text-white hover:bg-[#0e6b37]" : "bg-[#0e0e0e] text-white hover:bg-[#333]"}`}
              data-testid="enrichment-overview-worker-toggle"
            >
              {worker.enabled ? <PauseCircle className="w-3 h-3" /> : <PlayCircle className="w-3 h-3" />}
              {worker.enabled ? "Worker on" : "Worker paused"}
            </button>
          )}
          <button
            onClick={load}
            disabled={busy}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-[11px] mono uppercase tracking-wider border hairline hover:border-[#ff4500] hover:bg-[#fff4ee] disabled:opacity-50"
            data-testid="enrichment-overview-refresh"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
            Refresh
          </button>
        </div>
      </div>

      {!data ? (
        err ? (
          <AdminErrorState error={err} onRetry={load} label="the enrichment progress" />
        ) : (
          <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
        )
      ) : (
        <div className="border hairline overflow-hidden">
          {rows.map((r) => {
            const Icon = ICONS[r.id] || Bot;
            const isOpen = expanded === r.id;
            const complete = r.pct >= 100 && r.done > 0;
            return (
              <div key={r.id} className="border-b hairline last:border-b-0" data-testid={`enrichment-row-${r.id}`}>
                <button
                  type="button"
                  onClick={() => setExpanded((p) => (p === r.id ? null : r.id))}
                  className="w-full flex items-center gap-3 px-2.5 py-2 hover:bg-[#fafafa] transition-colors text-left"
                  aria-expanded={isOpen}
                >
                  <span className="text-[#666]">
                    {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  </span>
                  <Icon className={`w-4 h-4 shrink-0 ${complete ? "text-[#0a7f3f]" : "text-[#666]"}`} />
                  <div className="min-w-[130px] shrink-0">
                    <div className="text-xs font-bold leading-tight">{r.label}</div>
                    <div className="mono text-[9px] text-[#888] leading-tight">
                      {num(r.done)} / {num(r.total)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <ProgressBar pct={r.pct} done={r.done} />
                  </div>
                  <div className={`shrink-0 mono text-[11px] tabular-nums ${complete ? "text-[#0a7f3f] font-bold" : r.done > 0 ? "text-[#ff4500] font-bold" : "text-[#888]"}`}>
                    {r.pct.toFixed(1)}%
                    {complete && <CheckCircle2 className="inline w-3 h-3 ml-0.5 mb-0.5" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="px-2.5 pb-3 pt-1 bg-[#fafafa] border-t hairline text-[11px]" data-testid={`enrichment-detail-${r.id}`}>
                    <div className="grid grid-cols-3 gap-2 mb-2 mono text-[10px]">
                      <div><span className="text-[#888] uppercase tracking-wider text-[9px]">Done</span><div className="text-[#0e0e0e] font-bold text-sm">{num(r.done)}</div></div>
                      <div><span className="text-[#888] uppercase tracking-wider text-[9px]">Remaining</span><div className="text-[#ff4500] font-bold text-sm">{num(r.remaining)}</div></div>
                      <div><span className="text-[#888] uppercase tracking-wider text-[9px]">Total</span><div className="text-[#0e0e0e] font-bold text-sm">{num(r.total)}</div></div>
                    </div>
                    {worker && (r.id === "descriptions" || r.id === "facilities") && (
                      <div className="mono text-[10px] text-[#666] mb-2">
                        Worker: <strong className={worker.enabled ? "text-[#0a7f3f]" : "text-[#7f1d1d]"}>{worker.enabled ? "ON" : "PAUSED"}</strong>
                        {worker.enabled && (
                          <> · phase <strong>{worker.current_phase || "descriptions"}</strong> · session processed {num(worker.session_processed)}</>
                        )}
                      </div>
                    )}
                    <button
                      onClick={expandDataSection}
                      className="inline-flex items-center gap-1 text-[10px] mono uppercase tracking-wider text-[#ff4500] hover:underline"
                      data-testid={`enrichment-open-controls-${r.id}`}
                    >
                      Open full controls in Data &amp; Ingestion ↓
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-2 text-[10px] mono text-[#888]">
        30-s server-side cache · {num(data?.total_campsites)} campsites total.
      </p>
    </section>
  );
}
